/**
 * HTTP status for job detail when the listing still exists in storage but should be treated as gone.
 * Returns 410 when expired/inactive; undefined means serve as 200.
 */
import { parseLastSeenToUnixMs } from '../../lambda/utils/parse-last-seen-to-ms';
import { CRAWL_LAST_SEEN_FRESH_WINDOW_MS } from '../listing-crawl-freshness';

export type JobListingStalenessInput = {
  isActive?: boolean;
  postedDate?: string | number;
  lastSeen?: string | number;
  applicationDeadline?: string | number;
};

export function getJobListingHttpStatusCode(job: JobListingStalenessInput, nowMs: number): number | undefined {
  if (job.isActive === false) {
    return 410;
  }

  let httpStatusCode: number | undefined = undefined;

  let postedDate = job.postedDate;
  if (typeof postedDate === 'string') {
    postedDate = Date.parse(postedDate);
  }
  if (typeof postedDate === 'number' && !isNaN(postedDate)) {
    const THIRTY_FIVE_DAYS = 35 * 24 * 60 * 60 * 1000;
    if (nowMs - postedDate > THIRTY_FIVE_DAYS) {
      let lastSeenTimestamp: number | null = null;
      const lastSeen = job.lastSeen;

      if (lastSeen != null) {
        if (typeof lastSeen === 'string') {
          const parsed = Date.parse(lastSeen);
          if (!isNaN(parsed)) {
            lastSeenTimestamp = parsed;
          } else if (/^[0-9]+$/.test(lastSeen)) {
            lastSeenTimestamp = parseInt(lastSeen, 10);
          }
        } else if (typeof lastSeen === 'number') {
          lastSeenTimestamp = lastSeen;
        }
      }

      if (lastSeenTimestamp !== null && !isNaN(lastSeenTimestamp)) {
        const TWENTY_EIGHT_DAYS = 28 * 24 * 60 * 60 * 1000;
        if (nowMs - lastSeenTimestamp >= TWENTY_EIGHT_DAYS) {
          httpStatusCode = 410;
        }
      } else {
        httpStatusCode = 410;
      }
    }
  }

  if (httpStatusCode !== 410) {
    let applicationDeadline = job.applicationDeadline;
    if (typeof applicationDeadline === 'string') {
      applicationDeadline = Date.parse(applicationDeadline);
    }
    if (typeof applicationDeadline === 'number' && !isNaN(applicationDeadline)) {
      if (nowMs > applicationDeadline) {
        const lastSeenFreshMs = parseLastSeenToUnixMs(job.lastSeen);
        const crawlStillFresh =
          lastSeenFreshMs !== undefined && nowMs - lastSeenFreshMs <= CRAWL_LAST_SEEN_FRESH_WINDOW_MS;
        if (!crawlStillFresh) {
          httpStatusCode = 410;
        }
      }
    }
  }

  return httpStatusCode;
}
